/**
 * client_view_controller.js — address & contacts managers for the client detail
 * page (Stimulus).
 *
 * Ports static/js/client_view.js (attachOnce('clientViewListeners') + turbo:load,
 * plus the per-stream requestAnimationFrame re-init inside addressRequestStream /
 * contactRequestStream) to an element-scoped controller. The page has two
 * independent managers — the address manager ([data-client-address-manager]) and
 * the additional-contacts manager ([data-client-contacts-manager]) — each living
 * inside its own Turbo Stream target wrapper and each replaced independently on
 * create/update/delete.
 *
 * The controller hosts directly on each manager element and branches in connect()
 * on which manager it is attached to. Because each manager element is its own
 * stream target, a stream replace swaps in a fresh element; Stimulus pairs
 * disconnect() on the old element with connect() on the new one, re-binding the
 * handlers automatically. That is exactly what the old code's
 * requestAnimationFrame re-init (after each manager's own stream) was hand-
 * rolling, so it is gone here — the framework owns the re-init.
 *
 * The location map is owned by the separate Stimulus `detail-map` controller
 * (esm/detail_map_controller.js); this file never touches the map. Edit-client-
 * modal autocomplete is initialised by address_autocomplete.js via
 * turbo:frame-load, independent of this controller.
 *
 * Wired to window.siteApi (utils/site-api.js) and window.contactApi
 * (utils/contact-api.js), both backed by window.apiClient.
 *
 * data-controller="client-view" hosts on BOTH the [data-client-address-manager]
 * and [data-client-contacts-manager] .section-card elements.
 *
 * Source (to delete): static/js/client_view.js
 * Loader (page_assets.py): client_detail extra_js_files.
 */

import { Controller } from "@hotwired/stimulus";

    // ---- address manager ---------------------------------------------------

    function addressPayloadFromForm(form) {
        var formData = new FormData(form);
        var latitude = formData.get('latitude');
        var longitude = formData.get('longitude');
        return {
            label: formData.get('label') || null,
            address_input: formData.get('address_input') || null,
            street: formData.get('street') || null,
            city: formData.get('city') || null,
            state: formData.get('state') || null,
            postcode: formData.get('postcode') || null,
            latitude: latitude ? parseFloat(latitude) : null,
            longitude: longitude ? parseFloat(longitude) : null,
            is_primary: formData.get('is_primary') === 'on'
        };
    }

    // Thin wrapper around window.siteApi. A Turbo Stream result swaps in a
    // fresh manager element, which Stimulus re-connects automatically (no manual
    // re-init needed). Without Turbo, fall back to a full reload.
    async function addressRequestStream(operation) {
        var result;
        try {
            result = await operation();
        } catch (err) {
            if (!err.message) err.message = 'Address update failed';
            throw err;
        }
        if (result && result.turboStream && !window.Turbo) {
            window.location.reload();
        }
        return result;
    }

    function setAddressFormValue(form, name, value) {
        var field = form.elements[name];
        if (!field) return;
        if (field.type === 'checkbox') {
            field.checked = value === true || value === 'true';
            return;
        }
        field.value = value || '';
    }

    function clearAddressComponentFields(form) {
        ['street', 'city', 'state', 'postcode', 'latitude', 'longitude'].forEach(function (name) {
            setAddressFormValue(form, name, '');
        });
    }

    // `retries` is an argument, not a data-* attribute. As an attribute it was
    // written into the DOM, so Turbo cached it in the page snapshot: a page
    // restored with the counter already at the cap never retried again, and
    // the address field silently lost its autocomplete. Same defect as the
    // data-*-ready flags this controller used to guard its listeners with.
    function initClientAddressAutocomplete(form, retries) {
        if (!form) return;
        if (typeof window.AddressAutocomplete === 'undefined') {
            var attempt = retries || 0;
            // Stop chasing a form that has been swapped out from under us.
            if (attempt < 10 && form.isConnected) {
                setTimeout(function () {
                    initClientAddressAutocomplete(form, attempt + 1);
                }, 100);
            }
            return;
        }

        var input = form.querySelector('[data-client-address-autocomplete]');
        if (!input || input.autocompleteInstance) return;

        var autocompleteSelected = false;

        input.addEventListener('input', function () {
            if (autocompleteSelected) {
                autocompleteSelected = false;
                return;
            }
            clearAddressComponentFields(form);
        });

        input.autocompleteInstance = new window.AddressAutocomplete(input, {
            onSelect: function (suggestion) {
                autocompleteSelected = true;
                setAddressFormValue(form, 'street', suggestion.street);
                setAddressFormValue(form, 'city', suggestion.city);
                setAddressFormValue(form, 'state', suggestion.state);
                setAddressFormValue(form, 'postcode', suggestion.postcode);
                setAddressFormValue(form, 'latitude', suggestion.latitude);
                setAddressFormValue(form, 'longitude', suggestion.longitude);
            }
        });
    }

    function fillAddressEditForm(form, item) {
        setAddressFormValue(form, 'address_id', item.dataset.addressId);
        setAddressFormValue(form, 'label', item.dataset.addressLabel);
        setAddressFormValue(form, 'address_input', item.dataset.addressInput);
        setAddressFormValue(form, 'street', item.dataset.addressStreet);
        setAddressFormValue(form, 'city', item.dataset.addressCity);
        setAddressFormValue(form, 'state', item.dataset.addressState);
        setAddressFormValue(form, 'postcode', item.dataset.addressPostcode);
        setAddressFormValue(form, 'latitude', item.dataset.addressLatitude);
        setAddressFormValue(form, 'longitude', item.dataset.addressLongitude);
        setAddressFormValue(form, 'is_primary', item.dataset.addressPrimary);
    }

    function restoreAddressEditForm(manager, editForm) {
        if (!manager || !editForm) return;

        var editingItem = manager.querySelector('[data-address-editing="true"]');
        if (editingItem) {
            var display = editingItem.querySelector('[data-address-display]');
            if (display) display.classList.remove('d-none');
            delete editingItem.dataset.addressEditing;
        }

        var host = manager.querySelector('[data-address-edit-form-host]');
        if (host && editForm.parentNode !== host) {
            host.appendChild(editForm);
        }
        editForm.classList.add('d-none');
        editForm.reset();
    }

    function showAddressEditForm(manager, editForm, item) {
        restoreAddressEditForm(manager, editForm);
        fillAddressEditForm(editForm, item);

        var display = item.querySelector('[data-address-display]');
        if (display) display.classList.add('d-none');

        item.dataset.addressEditing = 'true';
        item.appendChild(editForm);
        editForm.classList.remove('d-none');
        editForm.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    function initClientSitesManager(manager, controller) {
        if (!manager) return;

        var clientId = manager.dataset.clientId;
        var form = manager.querySelector('[data-address-form]');
        var editForm = manager.querySelector('[data-address-edit-form]');
        var editCancel = manager.querySelector('[data-address-edit-cancel]');

        initClientAddressAutocomplete(form);
        initClientAddressAutocomplete(editForm);

        if (form) {
            controller.listen(form, 'submit', async function (event) {
                event.preventDefault();
                try {
                    await addressRequestStream(function () {
                        return window.siteApi.create(clientId, addressPayloadFromForm(form));
                    });
                } catch (error) {
                    if (typeof window.showErrorMessage === 'function') {
                        window.showErrorMessage(error.message);
                    } else {
                        alert(error.message);
                    }
                }
            });
        }

        if (editForm) {
            controller.listen(editForm, 'submit', async function (event) {
                event.preventDefault();
                var addressId = editForm.elements.address_id.value;
                try {
                    await addressRequestStream(function () {
                        return window.siteApi.update(addressId, addressPayloadFromForm(editForm));
                    });
                } catch (error) {
                    if (typeof window.showErrorMessage === 'function') {
                        window.showErrorMessage(error.message);
                    } else {
                        alert(error.message);
                    }
                }
            });
        }

        if (editCancel && editForm) {
            controller.listen(editCancel, 'click', function () {
                restoreAddressEditForm(manager, editForm);
            });
        }

        var addCancel = manager.querySelector('[data-address-add-cancel]');
        if (addCancel && form) {
            controller.listen(addCancel, 'click', function () {
                form.reset();
                form.classList.add('d-none');
            });
        }

        // The add form ships collapsed — below a list of cards an always-open
        // form reads as part of the last one. The header button reveals it.
        var addToggle = manager.querySelector('[data-address-add-toggle]');
        if (addToggle && form) {
            controller.listen(addToggle, 'click', function () {
                restoreAddressEditForm(manager, editForm);
                form.classList.remove('d-none');
                var input = form.querySelector('[data-client-address-autocomplete]');
                if (input) input.focus();
                form.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            });
        }

        controller.listen(manager, 'click', async function (event) {
            var button = event.target.closest('[data-address-action]');
            if (!button) return;

            var item = button.closest('[data-address-id]');
            if (!item) return;

            var addressId = item.dataset.addressId;
            var action = button.dataset.addressAction;

            try {
                if (action === 'delete') {
                    if (!confirm('Delete this address?')) return;
                    await addressRequestStream(function () {
                        return window.siteApi.remove(addressId);
                    });
                    return;
                }

                if (action === 'deactivate' || action === 'activate') {
                    await addressRequestStream(function () {
                        return window.siteApi.setActive(addressId, action === 'activate');
                    });
                    return;
                }

                if (action === 'edit') {
                    if (!editForm) return;
                    showAddressEditForm(manager, editForm, item);
                }
            } catch (error) {
                if (typeof window.showErrorMessage === 'function') {
                    window.showErrorMessage(error.message);
                } else {
                    alert(error.message);
                }
            }
        });
    }

    // ---- additional-contacts manager ---------------------------------------
    // Mirrors the address manager above, wired to window.contactApi
    // (utils/contact-api.js). No address autocomplete; plain first/last name,
    // email, phone, role fields.

    function contactPayloadFromForm(form) {
        var formData = new FormData(form);
        return {
            first_name: formData.get('first_name') || null,
            last_name: formData.get('last_name') || null,
            email: formData.get('email') || null,
            phone: formData.get('phone') || null,
            role: formData.get('role') || null,
            is_primary: formData.get('is_primary') === 'on'
        };
    }

    async function contactRequestStream(operation) {
        var result;
        try {
            result = await operation();
        } catch (err) {
            if (!err.message) err.message = 'Contact update failed';
            throw err;
        }
        if (result && result.turboStream && !window.Turbo) {
            window.location.reload();
        }
        return result;
    }

    function setContactFormValue(form, name, value) {
        var field = form.elements[name];
        if (!field) return;
        if (field.type === 'checkbox') {
            field.checked = value === true || value === 'true';
            return;
        }
        field.value = value || '';
    }

    function fillContactEditForm(form, item) {
        setContactFormValue(form, 'contact_id', item.dataset.contactId);
        setContactFormValue(form, 'first_name', item.dataset.contactFirstName);
        setContactFormValue(form, 'last_name', item.dataset.contactLastName);
        setContactFormValue(form, 'email', item.dataset.contactEmail);
        setContactFormValue(form, 'phone', item.dataset.contactPhone);
        setContactFormValue(form, 'role', item.dataset.contactRole);
        setContactFormValue(form, 'is_primary', item.dataset.contactPrimary);
    }

    function restoreContactEditForm(manager, editForm) {
        if (!manager || !editForm) return;

        var editingItem = manager.querySelector('[data-contact-editing="true"]');
        if (editingItem) {
            var display = editingItem.querySelector('[data-contact-display]');
            if (display) display.classList.remove('d-none');
            delete editingItem.dataset.contactEditing;
        }

        var host = manager.querySelector('[data-contact-edit-form-host]');
        if (host && editForm.parentNode !== host) {
            host.appendChild(editForm);
        }
        editForm.classList.add('d-none');
        editForm.reset();
    }

    function showContactEditForm(manager, editForm, item) {
        restoreContactEditForm(manager, editForm);
        fillContactEditForm(editForm, item);

        var display = item.querySelector('[data-contact-display]');
        if (display) display.classList.add('d-none');

        item.dataset.contactEditing = 'true';
        item.appendChild(editForm);
        editForm.classList.remove('d-none');
        editForm.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    function showContactAddForm(manager) {
        var form = manager.querySelector('[data-contact-form]');
        var toggle = manager.querySelector('[data-contact-add-toggle]');
        if (toggle) toggle.classList.add('d-none');
        if (form) {
            form.classList.remove('d-none');
            var firstInput = form.querySelector('input[name="first_name"]');
            if (firstInput) firstInput.focus();
        }
    }

    function hideContactAddForm(manager) {
        var form = manager.querySelector('[data-contact-form]');
        var toggle = manager.querySelector('[data-contact-add-toggle]');
        if (form) {
            form.reset();
            form.classList.add('d-none');
        }
        if (toggle) toggle.classList.remove('d-none');
    }

    function initClientContactsManager(manager, controller) {
        if (!manager) return;

        var clientId = manager.dataset.clientId;
        var form = manager.querySelector('[data-contact-form]');
        var editForm = manager.querySelector('[data-contact-edit-form]');
        var editCancel = manager.querySelector('[data-contact-edit-cancel]');

        if (form) {
            controller.listen(form, 'submit', async function (event) {
                event.preventDefault();
                try {
                    await contactRequestStream(function () {
                        return window.contactApi.create(clientId, contactPayloadFromForm(form));
                    });
                } catch (error) {
                    if (typeof window.showErrorMessage === 'function') {
                        window.showErrorMessage(error.message);
                    } else {
                        alert(error.message);
                    }
                }
            });
        }

        if (editForm) {
            controller.listen(editForm, 'submit', async function (event) {
                event.preventDefault();
                var contactId = editForm.elements.contact_id.value;
                try {
                    await contactRequestStream(function () {
                        return window.contactApi.update(contactId, contactPayloadFromForm(editForm));
                    });
                } catch (error) {
                    if (typeof window.showErrorMessage === 'function') {
                        window.showErrorMessage(error.message);
                    } else {
                        alert(error.message);
                    }
                }
            });
        }

        if (editCancel && editForm) {
            controller.listen(editCancel, 'click', function () {
                restoreContactEditForm(manager, editForm);
            });
        }

        controller.listen(manager, 'click', async function (event) {
            if (event.target.closest('[data-contact-add-toggle]')) {
                showContactAddForm(manager);
                return;
            }
            if (event.target.closest('[data-contact-add-cancel]')) {
                hideContactAddForm(manager);
                return;
            }

            var button = event.target.closest('[data-contact-action]');
            if (!button) return;

            var item = button.closest('[data-contact-id]');
            if (!item) return;

            var contactId = item.dataset.contactId;
            var action = button.dataset.contactAction;

            try {
                if (action === 'delete') {
                    if (!confirm('Delete this contact?')) return;
                    await contactRequestStream(function () {
                        return window.contactApi.remove(contactId);
                    });
                    return;
                }

                if (action === 'edit') {
                    if (!editForm) return;
                    showContactEditForm(manager, editForm, item);
                }
            } catch (error) {
                if (typeof window.showErrorMessage === 'function') {
                    window.showErrorMessage(error.message);
                } else {
                    alert(error.message);
                }
            }
        });
    }

    // ---- controller --------------------------------------------------------
    // Hosts on each manager element; connect() branches on which one it is, so a
    // single controller name drives both the address and the contacts sections.

export default class extends Controller {
        connect() {
            this._listeners = [];
            if (this.element.hasAttribute('data-client-address-manager')) {
                initClientSitesManager(this.element, this);
            } else if (this.element.hasAttribute('data-client-contacts-manager')) {
                initClientContactsManager(this.element, this);
            }
        }

        disconnect() {
            (this._listeners || []).forEach(function (entry) {
                entry[0].removeEventListener(entry[1], entry[2]);
            });
            this._listeners = [];
        }

        /**
         * Bind a listener that disconnect() will remove.
         *
         * This replaces a set of data-*-ready attributes that used to guard
         * against double-binding. Those were worse than unnecessary: Stimulus
         * already gives exactly one connect() per element instance, and because
         * the flags were written into the DOM, Turbo serialised them into its
         * page-cache snapshot. A restored page therefore arrived already
         * flagged, the fresh controller skipped binding entirely, and Edit /
         * Deactivate / Delete were dead until a hard reload discarded the
         * snapshot — intermittent exactly in proportion to how often a visit
         * came from the cache.
         */
        listen(element, type, handler) {
            if (!element) return;
            element.addEventListener(type, handler);
            this._listeners.push([element, type, handler]);
        }
}
